import React from 'react';
import {
  createStyles,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Theme
} from '@material-ui/core';
import Clear from '@material-ui/icons/Clear';
import EditIcon from '@material-ui/icons/Edit';
import {useState} from 'react';
import clsx from 'clsx';
import { PromptDialog } from './dialogs';
import { ArrowTooltip } from './tooltips';
import { EnhancedIconButton } from './buttons';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      fontWeight: "bold",
      userSelect: "none"
    },
    denseCell: {
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1.0)}px !important`,
    },
    footerDense: {
      height: theme.spacing(4),
      minHeight: "0px",
      overflow: "hidden"
    },
    hiddenSort: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    noSelect: {
      userSelect: "none"
    },
    rowDisabled: {
      "& th": {
        color: "lightgray"
      },
      "& td": {
        color: "lightgray"
      }
    },
    break: {
      wordBreak: "break-all"
    },
    pointer: {
      cursor: "pointer"
    },
    center: {
      textAlign: "center"
    },
    rowIcon: {
      fontSize: "1em"
    },
    tooltipDiv: {
      width: "fit-content"
    }
  }),
);

const EnhancedTableHead = (props) => {
  const {classes, order, orderBy, onRequestSort, columns,
    deletable, editable, isDense, isSortable} = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const cellClassName = isDense ? classes.denseCell : "";

  return (
    <TableHead>
      <TableRow>
        {columns.map((column, index) => (
          <TableCell
            key={column.id}
            align={column.center ? 'center' : "left"}
            sortDirection={orderBy === column.id ? order : false}
            className={clsx(classes.header, cellClassName)}
          >
            {/* Non-sortable column header */}
            {(isSortable === false) && (column.label)}

            {/* Sortable column header */}
            {(isSortable === true) && (<TableSortLabel
              active={orderBy === column.id}
              direction={orderBy === column.id ? order : 'asc'}
              onClick={createSortHandler(column.id)}
            >
              {column.label}
              {orderBy === column.id ? (
                <span className={classes.hiddenSort}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>)}
          </TableCell>
        ))}
        {deletable && (<TableCell
          key={"delete"}
          align={'right'}
          className={classes.header}
        />)}
        {editable && (<TableCell
          key={"edit"}
          align={'right'}
          className={classes.header}
        />)}
      </TableRow>
    </TableHead>
  );
};

interface EnhancedTableProps {
  columns: {id: string, numeric: boolean, label: string};
}

const EnhancedTable = (props: EnhancedTableProps | any) => {
  const {
    columns, rows, rowType, onRowClick, deletable, editable,
    onDeleteClick, onEditClick, dense, pageSize, defaultSort, sortable,
    wordBreak, ...other} = props;

  const classes = useStyles();

  const numRowsPerPage = pageSize !== undefined ? pageSize : 5;
  const isSortable = sortable !== undefined ? sortable : true;

  const [open, setOpen] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(defaultSort);
  const [selectedRow, setSelectedRow] = useState();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(numRowsPerPage);

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const descendingComparator = (a, b, orderBy) => {
    let compA = a[orderBy];
    let compB = b[orderBy];

    if (typeof compA === "string") {
      compA = compA.toLowerCase();
    }

    if (typeof compB === "string") {
      compB = compB.toLowerCase();
    }

    if (compB < compA) {
      return -1;
    } else if (compB > compA) {
      return 1;
    } else {
      return 0;
    }
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  // Make sure our max page is valid
  const maxPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
  if (page > maxPage) {
    setPage(maxPage);
  }

  if (isSortable) {
    // Sort based on sort criteria
    rows.sort(getComparator(order, orderBy));
  }

  let sliced = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const isDense = dense === true;
  const cellClassName = isDense ? classes.denseCell : "";
  const footerClassName = isDense ? classes.footerDense : "";

  const cellContent = (column, content, condition) => {
    if (typeof(content) === "string") {
      content = condition ? content : `${content.slice(0,100)}...`;
    }

    if (column.tooltip) {
      return (
        <ArrowTooltip title={column.tooltip}>
          <div className={classes.tooltipDiv}>
            {content}
          </div>
        </ArrowTooltip>
      );
    }

    return content;
  };

  const cellTitle = (column, content) => {
    // Ignore standard title for if tooltip exists
    if (column.tooltip) {
      return "";
    }

    return (typeof(content) === "string") ? content : "";
  };

  return (
    <div {...other}>
      <Paper>
        <TableContainer>
          <Table
            size={'small'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              columns={columns}
              deletable={deletable}
              editable={editable}
              isDense={isDense}
              isSortable={isSortable}
            />
            <TableBody>
              {sliced.map(row => {
                const isItemSelected = selectedRow === row;
                const disabled = row.disabled === true;

                return (
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                    className={clsx(classes.noSelect, {
                      [classes.rowDisabled]: disabled,
                    })}
                  >
                    {columns.map((column, index) => {
                      const key = `${row.id}${column.id}`;
                      const first = index === 0;
                      const text = row[column.id];
                      const condition = (column.numeric || text.length <= 100);

                      return (<TableCell
                        key={key}
                        component={first ? "th" : null}
                        scope={first ? "row" : ""}
                        align={"left"}
                        onClick={() => {
                          if (disabled) {
                            return;
                          }

                          if (onRowClick) {
                            onRowClick(row, index);
                          }
                        }}
                        className={clsx(cellClassName, {
                          [classes.break]: wordBreak,
                          [classes.pointer]: column.pointer,
                          [classes.center]: column.center
                        })}
                        title={cellTitle(column, text)}
                      >
                        {cellContent(column, text, condition)}
                      </TableCell>);
                    })}

                    {deletable && disabled && (
                      <TableCell className={cellClassName}/>
                    )}

                    {/* Delete button */}
                    {deletable && (disabled===false) && (
                      <TableCell align="right" className={cellClassName}>
                        <EnhancedIconButton
                          title="Delete row"
                          onClick={() => {
                            setOpen(true);
                            setSelectedRow(row);
                          }}
                        >
                          <Clear
                            color="secondary"
                            fontSize="small"
                            className={classes.rowIcon}
                          />
                        </EnhancedIconButton>
                      </TableCell>
                    )}

                    {/* Edit button */}
                    {editable && (
                      <TableCell align="right" className={cellClassName}>
                        <EnhancedIconButton
                          title="Edit row"
                          onClick={() => {
                            onEditClick(row);
                          }}
                        >
                          <EditIcon
                            fontSize="small"
                            className={classes.rowIcon}
                          />
                        </EnhancedIconButton>
                      </TableCell>
                    )}

                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          classes={{
            toolbar: footerClassName
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Delete Dialog */}
      <PromptDialog
        open={open}
        onCancel={handleClose}
        onSubmit={() => {
          onDeleteClick(selectedRow);
          handleClose();
        }}
        title="Delete entry?"
        prompt={`Do you want to delete this ${rowType}?`}
        button="Delete"
      />
    </div>
  );
};

export default EnhancedTable;